import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

// Connects to data-controller="admin-area-chart"
export default class extends Controller {
  static targets = ["chart", "periodButtons"]
  static values = {
    series: Array,
    categories: Array,
    colors: Array,
    height: { type: Number, default: 335 },
    curve: { type: String, default: "straight" },
    showMarkers: { type: Boolean, default: true },
    showGrid: { type: Boolean, default: true },
    gradient: { type: Boolean, default: false },
    yAxisMin: Number,
    yAxisMax: Number
  }

  connect() {
    this.initChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  }

  initChart() {
    const options = this.buildOptions()
    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }

  buildOptions() {
    const options = {
      series: this.seriesValue,
      legend: {
        show: false,
        position: "top",
        horizontalAlign: "left"
      },
      colors: this.colorsValue,
      chart: {
        fontFamily: "Satoshi, sans-serif",
        height: this.heightValue,
        type: "area",
        toolbar: {
          show: false
        }
      },
      responsive: [
        {
          breakpoint: 1024,
          options: {
            chart: {
              height: 300
            }
          }
        },
        {
          breakpoint: 1366,
          options: {
            chart: {
              height: 350
            }
          }
        }
      ],
      stroke: {
        width: this.gradientValue ? [3.5, 3.5] : [2, 2],
        curve: this.curveValue
      },
      markers: {
        size: this.showMarkersValue ? 4 : 0,
        colors: "#fff",
        strokeColors: this.colorsValue,
        strokeWidth: 3,
        strokeOpacity: 0.9,
        strokeDashArray: 0,
        fillOpacity: 1,
        discrete: [],
        hover: {
          size: undefined,
          sizeOffset: 5
        }
      },
      grid: {
        borderColor: "rgba(226, 232, 240, 0.1)",
        strokeDashArray: this.gradientValue ? 7 : 0,
        xaxis: {
          lines: {
            show: this.showGridValue
          }
        },
        yaxis: {
          lines: {
            show: this.showGridValue
          }
        }
      },
      dataLabels: {
        enabled: false
      },
      xaxis: {
        type: "category",
        categories: this.categoriesValue,
        axisBorder: {
          show: false
        },
        axisTicks: {
          show: false
        },
        labels: {
          style: {
            colors: "#64748B",
            fontSize: "12px",
            fontFamily: "Satoshi, sans-serif"
          }
        }
      },
      yaxis: {
        title: {
          style: {
            fontSize: "0px"
          }
        },
        labels: {
          style: {
            colors: "#64748B",
            fontSize: "12px",
            fontFamily: "Satoshi, sans-serif"
          }
        }
      }
    }

    // Add gradient fill for style2/style3
    if (this.gradientValue) {
      options.fill = {
        gradient: {
          enabled: true,
          opacityFrom: 0.55,
          opacityTo: 0
        }
      }
    } else {
      // Add drop shadow for style1
      options.chart.dropShadow = {
        enabled: true,
        color: "#623CEA14",
        top: 10,
        blur: 4,
        left: 0,
        opacity: 0.1
      }
    }

    // Set y-axis min/max if provided
    if (this.hasYAxisMinValue) {
      options.yaxis.min = this.yAxisMinValue
    }
    if (this.hasYAxisMaxValue) {
      options.yaxis.max = this.yAxisMaxValue
    }

    return options
  }

  selectPeriod(event) {
    const button = event.currentTarget
    const period = button.dataset.period

    // Update button styles
    if (this.hasPeriodButtonsTarget) {
      const buttons = this.periodButtonsTarget.querySelectorAll("button")
      buttons.forEach(btn => {
        if (btn === button) {
          btn.className = "rounded bg-white px-3 py-1 text-xs font-medium text-black shadow-card hover:bg-white hover:shadow-card dark:bg-boxdark dark:text-white dark:hover:bg-boxdark"
        } else {
          btn.className = "rounded px-3 py-1 text-xs font-medium text-black hover:bg-white hover:shadow-card dark:text-white dark:hover:bg-boxdark"
        }
      })
    }

    // Dispatch custom event for period change
    this.dispatch("periodChange", { detail: { period } })
  }

  // Public method to update chart data
  updateSeries(newSeries) {
    if (this.chart) {
      this.chart.updateSeries(newSeries)
    }
  }

  // Public method to update categories
  updateCategories(newCategories) {
    if (this.chart) {
      this.chart.updateOptions({
        xaxis: {
          categories: newCategories
        }
      })
    }
  }

  // Public method to update both series and categories
  updateData(newSeries, newCategories) {
    if (this.chart) {
      this.chart.updateOptions({
        series: newSeries,
        xaxis: {
          categories: newCategories
        }
      })
    }
  }

  // Public method to toggle series visibility
  toggleSeries(seriesName) {
    if (this.chart) {
      this.chart.toggleSeries(seriesName)
    }
  }
}
